/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, chaves e valores técnicos do fio, não texto de UI */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import type { Cents } from '@/shared/format/money';
import { creditApi } from '@/shared/http';

import type { CreditNote } from './credit-note.wire';

// Trilha "liquidação" (docs/execucao/CREDIT-NOTE-LIQUIDACAO-RELATORIO.md): o fio do roteiro de liquidação
// e da conta transitória que ainda não estava em `credit-note.api.ts`, os corpos montados como o legado
// (U02) e as regras que os formulários do legado calculam. Arquivo:linha do legado ao lado de cada peça.

const BASE = '/CreditNote';

function parseWire<S extends z.ZodType>(schema: S, data: unknown, label: string): z.output<S> {
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn(`${label} fora do contrato`, parsed.error.issues);
  return data as z.output<S>;
}

const text = z.string().nullish();
const numberish = z.union([z.number(), z.string()]).nullish();

// ─── fio ────────────────────────────────────────────────────────────────────

/** `LiquidationScheduleReadModel` (services/LiquidationSchedule/types/LiquidationScheduleReadModel.ts:10-52). */
export const scheduleItemSchema = z.looseObject({
  id: z.string(),
  amountInCents: z.number().nullish(),
  informedDueDate: text,
  pixQrCode: z.looseObject({ qrCode: text }).nullish(),
  bankSlip: z.looseObject({ barcodeOrDigitableLine: text }).nullish(),
  pixKey: z.looseObject({ key: text, pixKeyType: numberish, typeDisplay: text }).nullish(),
  transfer: z
    .looseObject({
      to: z
        .looseObject({
          name: z.string().nullish().meta({ pii: 'name' }),
          registrationNumber: z.string().nullish().meta({ pii: 'document' }),
          bankCode: numberish,
          bankCodeDisplay: text,
          bankIspb: numberish,
          agency: text,
          accountNumber: text,
          accountType: text,
        })
        .nullish(),
      type: numberish,
      typeDisplay: text,
    })
    .nullish(),
  description: text,
  retrievedData: z
    .looseObject({
      beneficiary: z.looseObject({ name: z.string().nullish().meta({ pii: 'name' }), registrationNumber: z.string().nullish().meta({ pii: 'document' }) }).nullish(),
      bankslipData: z.looseObject({ dueDate: text }).nullish(),
    })
    .nullish(),
  type: text,
  typeDisplay: text,
  linkStatus: text,
  linkStatusDisplay: text,
  status: text,
  statusDisplay: text,
  paymentType: numberish,
  erroMessage: text,
  failureMessage: text,
  cosifAccountCode: text,
});
export type ScheduleItem = z.infer<typeof scheduleItemSchema>;

/** Os itens do roteiro no formato completo (o schema do `GET` em `credit-note.wire.ts` só lê o que a barra usa). */
export function scheduleItemsOf(items: readonly unknown[] | null | undefined): ScheduleItem[] {
  // Em liquidação simples a CreditAPI manda `null` por item (MapperProfile.cs:2356-2360).
  return (items ?? []).filter((item) => typeof item === 'object' && item !== null).map((item) => parseWire(scheduleItemSchema, item, 'item do roteiro de liquidação'));
}

export type ScheduleAction = 'NoAction' | 'ManualLiquidation' | 'AutomaticLiquidation' | 'Failed';
export type TransitoryAction = 'ManualLiquidation' | 'AutomaticLiquidation';

/** `ApprovalLiquidationScheduleCreateModel` (ApprovalLiquidationScheduleRequest.ts:4-15). */
export interface ApproveScheduleBody {
  cosifAccountCode?: string | null;
  transitoryBankAccountAction?: TransitoryAction | null;
  liquidationSchedule?: ApproveScheduleItemBody[];
}
export interface ApproveScheduleItemBody {
  liquidationScheduleId: string;
  action: ScheduleAction;
  failureMessage?: string;
  cosifAccountCode?: string | null;
}

/** `approveLiquidationSchedule` (LiquidationSchedule.ts:55-65). */
export async function approveLiquidationSchedule(id: string, body: ApproveScheduleBody) {
  return creditApi.post(`${BASE}/${id}/approveLiquidationSchedule`, body);
}

/** `BankAccountCreateOrUpdateModel` (utils/interface/index.ts:110-122), na ordem da classe. */
type BankAccountKey = 'id' | 'bankCode' | 'bankIspb' | 'account' | 'accountDigit' | 'agency' | 'agencyDigit' | 'type' | 'jointAccount' | 'operationTypeValue' | 'pixKeyTypeValue' | 'keyPix';
export type ClosureRevisionAccount = Partial<Record<BankAccountKey, unknown>>;

/** `postClosureRevisionAsync` (LiquidationSchedule.ts:125-134): `{ changeBankAccount }`. */
export async function closeTransitoryAccountRevision(id: string, account: ClosureRevisionAccount) {
  return creditApi.post(`${BASE}/${id}/liquidationScheduleClosureRevision`, { changeBankAccount: account });
}

// ─── regras do roteiro ──────────────────────────────────────────────────────

export interface CosifOption {
  readonly code: string;
  readonly description: string;
  readonly externalAccountId?: string | null | undefined;
}

/** `isFinishedAndUnlinked` (types/utils/index.tsx:285-287). */
const finishedAndUnlinked = (item: ScheduleItem) => item.status === 'Finished' && item.linkStatus === 'Unlinked';

/**
 * O que o drawer "Aprovações de roteiro de liquidação" calcula antes do formulário
 * (ApproveLiquidationScheduleContainer.tsx:41, 73-96): conta transitória, transferência transitória já
 * paga e desvinculada, o COSIF dela e os itens que aparecem (Novo e Vinculado).
 */
export function scheduleFacts(note: CreditNote, items: readonly ScheduleItem[]) {
  const unlinked = items.find((item) => item.type === 'TransferTransitoryBankAccount' && finishedAndUnlinked(item));
  return {
    hasTransitoryAccount: !!note.transitoryBankAccountId,
    hasUnlinkedTransfer: unlinked !== undefined,
    hasCosifAccountOfTransitory: !!unlinked?.cosifAccountCode,
    pending: items.filter((item) => item.status === 'New' && item.linkStatus === 'Linked'),
  };
}
export type ScheduleFacts = ReturnType<typeof scheduleFacts>;

export interface ApproveItemValues {
  liquidationScheduleId: string;
  action: ScheduleAction | null;
  cosifAccountCode: string | null;
  failureMessage: string;
}
export interface ApproveScheduleValues {
  cosifAccountCode: string | null;
  transitoryBankAccountAction: TransitoryAction | null;
  liquidationSchedule: ApproveItemValues[];
}

/** O que muda com a escolha: os `watch` do wrapper e de cada item (Form/*.tsx). */
export function approveState(facts: ScheduleFacts, cosif: readonly CosifOption[], values: Pick<ApproveScheduleValues, 'cosifAccountCode'>) {
  const external = (code: string | null | undefined) => !!code && !!cosif.find((item) => item.code === code)?.externalAccountId;
  const principalExternal = external(values.cosifAccountCode);
  // `hasExternalId` do container: nasce do COSIF da transferência transitória e segue o COSIF principal escolhido (:81-85, :104-120).
  const hasExternalId = values.cosifAccountCode ? principalExternal : facts.hasCosifAccountOfTransitory;
  return {
    external,
    principalExternal,
    /** "Conta de débito" de cada item (WrapperForm.tsx:53). */
    showItemCosif: !principalExternal && !facts.hasCosifAccountOfTransitory && !facts.hasTransitoryAccount,
    /** "Liquidar automaticamente" da conta transitória (WrapperForm.tsx:46-51). */
    transitoryAutomatic: !values.cosifAccountCode || principalExternal,
    /** Os itens só são validados sem conta transitória e sem COSIF principal com conta externa (Schema.ts:28). */
    validateItems: !facts.hasTransitoryAccount && !principalExternal,
    itemCosifRequired: !hasExternalId && !facts.hasTransitoryAccount,
    /** "Liquidar automaticamente" de um item (ApproveLiquidationScheduleForm.tsx:40-43). */
    itemAutomatic: (itemCosif: string | null) => external(itemCosif) || facts.hasCosifAccountOfTransitory || facts.hasTransitoryAccount || principalExternal,
  };
}

/** `defaultValuesApproveLiquidationScheduleSchema` + o `append` de cada item Novo e Vinculado (Schema.ts:58-62; Form.tsx:127-140). */
export function approveDefaults(facts: ScheduleFacts): ApproveScheduleValues {
  return {
    cosifAccountCode: null,
    transitoryBankAccountAction: 'AutomaticLiquidation',
    liquidationSchedule: facts.pending.map((item) => ({ liquidationScheduleId: item.id, action: 'NoAction', cosifAccountCode: null, failureMessage: '' })),
  };
}

/**
 * Corpo do `POST approveLiquidationSchedule`: o `toDataModel` do `ApprovalLiquidationScheduleCreateModel`
 * sobre os valores do formulário (Container.tsx:58-71). O COSIF de cada item só existe quando o campo
 * aparece e sai de todos quando o principal tem conta externa; "Observações" só com "Reprovar".
 */
export function approveScheduleBody(values: ApproveScheduleValues, facts: ScheduleFacts, cosif: readonly CosifOption[]): ApproveScheduleBody {
  const state = approveState(facts, cosif, values);
  return {
    cosifAccountCode: values.cosifAccountCode,
    transitoryBankAccountAction: values.transitoryBankAccountAction,
    liquidationSchedule: values.liquidationSchedule.map((item) => ({
      liquidationScheduleId: item.liquidationScheduleId,
      action: item.action ?? 'NoAction',
      ...(item.action === 'Failed' && item.failureMessage !== '' ? { failureMessage: item.failureMessage } : {}),
      ...(state.showItemCosif && !state.principalExternal ? { cosifAccountCode: item.cosifAccountCode } : {}),
    })),
  };
}

/** Total dos itens, para a prévia. */
export const scheduleTotal = (items: readonly ScheduleItem[]): Cents => items.reduce((sum, item) => sum + Math.round(item.amountInCents ?? 0), 0);

// ─── rótulos do roteiro ─────────────────────────────────────────────────────

const piece = (value: unknown) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const invalidBankCode = (code: unknown) => code === '0' || code === 0 || code === undefined || code === null;

/** `formatTransfer` (types/utils/index.tsx:243-249), sem as quebras de linha do template. */
function transferText(to: NonNullable<NonNullable<ScheduleItem['transfer']>['to']> | null | undefined, nd: string) {
  const bank = invalidBankCode(to?.bankCode) ? '' : piece(to?.bankCode);
  return `${bank} - ${to?.bankCodeDisplay ?? nd} - Ag: ${piece(to?.agency)} - Conta: ${piece(to?.accountNumber)}`.replace(/\s+/g, ' ').trim();
}

export interface ScheduleLabel {
  /** Forma de pagamento (`description` do legado): "Pagamento por TED". */
  readonly method: MessageDescriptor | null;
  /** Prefixo do rótulo e o dado ("Transferência: 341 - Itaú - Ag: 1234 - Conta: 5678-9"). */
  readonly kind: MessageDescriptor | null;
  readonly detail: string;
}

/** `createLiquidationScheduleOptions` (types/utils/index.tsx:201-241): forma e rótulo de um item do roteiro. */
export function scheduleLabel(item: ScheduleItem, nd: string): ScheduleLabel {
  if (item.pixQrCode) return { method: msg`Pagamento por QR Code`, kind: msg`QR Code`, detail: piece(item.pixQrCode.qrCode) };
  if (item.bankSlip) return { method: msg`Pagamento por Boleto`, kind: msg`Boleto`, detail: piece(item.bankSlip.barcodeOrDigitableLine) };
  if (item.pixKey) {
    if (Number(item.pixKey.pixKeyType) === 6 && item.transfer) return { method: msg`Pagamento por Pix - Agência e Conta`, kind: msg`Pix (Agência e Conta)`, detail: transferText(item.transfer.to, nd) };
    return { method: msg`Pagamento por Pix`, kind: msg`Chave Pix`, detail: piece(item.pixKey.key) };
  }
  if (item.transfer) return { method: msg`Pagamento por TED`, kind: msg`Transferência`, detail: transferText(item.transfer.to, nd) };
  return { method: null, kind: null, detail: nd };
}

/** Beneficiário do cartão do roteiro (`mapDataToCardValue`, types/utils/index.tsx:36-142). */
export function scheduleBeneficiary(item: ScheduleItem): { name: string | null; document: string | null } {
  const to = item.transfer?.to;
  const useTransfer = !!item.transfer && (!item.pixQrCode && !item.bankSlip) && (!item.pixKey || Number(item.pixKey.pixKeyType) === 6);
  const name = useTransfer ? to?.name : item.retrievedData?.beneficiary?.name;
  const document = useTransfer ? to?.registrationNumber : item.retrievedData?.beneficiary?.registrationNumber;
  return { name: name ?? null, document: document ?? null };
}

// ─── conta transitória ──────────────────────────────────────────────────────

/** "Encerrar conta transitória" (LiquidationScheduleClosureSchema.ts:4-20): o `date()` do yup vira ISO UTC da meia-noite de -03:00. */
export interface CloseTransitoryValues {
  paymentDate: string | null;
  cosifAccountCode: string | null;
  closeTransitoryBankAccount: boolean;
}

export const closeTransitoryBody = (values: CloseTransitoryValues) => ({
  cosifAccountCode: values.cosifAccountCode ?? '',
  closeTransitoryBankAccount: values.closeTransitoryBankAccount,
  paymentDate: values.paymentDate ? new Date(`${values.paymentDate}T00:00:00-03:00`).toISOString() : '',
});

/** Valores do `BankDetails` (BankDetailsSchema.ts:14-116): texto como o formulário guarda. */
export interface BankDetailsValues {
  id: string | null;
  operationTypeValue: string | null;
  pixKeyTypeValue: string | null;
  keyPix: string | null;
  bankCode: string | null;
  bankCodeDisplay: string | null;
  bankIspb: number | null;
  type: string | null;
  agency: string | null;
  agencyDigit: string | null;
  account: string | null;
  accountDigit: string | null;
  jointAccount: boolean | null;
}

const optionalText = (value: unknown): string | null => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null);
const optionalNumber = (value: unknown): number | null => (typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : null);

/** O formulário nasce com a `changeBankAccount` do `GET` (LiquidationScheduleClosureRevision/index.tsx:25-26, 74). */
export function bankDetailsDefaults(account: unknown): BankDetailsValues {
  const item = account !== null && typeof account === 'object' ? (account as Record<string, unknown>) : {};
  return {
    id: optionalText(item['id']),
    operationTypeValue: optionalText(item['operationTypeValue']),
    pixKeyTypeValue: optionalText(item['pixKeyTypeValue']),
    keyPix: optionalText(item['keyPix']),
    bankCode: optionalText(item['bankCode']),
    bankCodeDisplay: optionalText(item['bankCodeDisplay']),
    bankIspb: optionalNumber(item['bankIspb']),
    type: optionalText(item['type']),
    agency: optionalText(item['agency']),
    agencyDigit: optionalText(item['agencyDigit']),
    account: optionalText(item['account']),
    accountDigit: optionalText(item['accountDigit']),
    jointAccount: typeof item['jointAccount'] === 'boolean' ? item['jointAccount'] : null,
  };
}

/** `clearOperationTypeFields` (helpers/methods/ClearOperationTypeFields.ts:3-17). */
export function clearedForOperation(operation: string | null): Partial<BankDetailsValues> {
  return operation === 'Transfer' ? { keyPix: null, pixKeyTypeValue: null } : { type: null, bankCode: null, bankCodeDisplay: null, agency: null, accountDigit: null, account: null };
}

/** `react-text-mask` com `guide` e `keepCharPositions` (MaskedInput.tsx): a chave editada vai mascarada. */
const MASKS: Readonly<Record<string, string>> = {
  Phone: '(##) #####-####',
  NaturalRegistrationNumber: '###.###.###-##',
  LegalRegistrationNumber: '##.###.###/####-##',
};
function legacyMasked(raw: string, pattern: string): string {
  const chars = raw.replace(/[^0-9A-Za-z]/g, '').split('');
  let out = '';
  for (const slot of pattern) {
    if (chars.length === 0) break;
    out += slot === '#' ? (chars.shift() ?? '') : slot;
  }
  return out;
}

/**
 * Corpo do "Revisar conta transitória" (LiquidationScheduleClosureRevision/index.tsx:40-68): TED ou Pix
 * agência e conta levam os dados da conta (banco e ISPB em número, conta em texto); os outros Pix, só a
 * chave. Os casts são os do yup (`account` é `number()`). A chave de telefone, CPF ou CNPJ **editada** vai
 * com a máscara do campo do legado; sem edição, como veio do `GET`.
 */
export function closureRevisionAccount(values: BankDetailsValues, initialKeyPix: string | null): ClosureRevisionAccount {
  const mask = values.pixKeyTypeValue ? MASKS[values.pixKeyTypeValue] : undefined;
  const keyPix = values.keyPix !== null && values.keyPix !== initialKeyPix && mask ? legacyMasked(values.keyPix, mask) : values.keyPix;
  // `op === 'Transfer' || op === EOperationType.Transfer` (1).
  const isTransfer = values.operationTypeValue === 'Transfer' || values.operationTypeValue === '1';
  const isPixAgency = values.pixKeyTypeValue === 'AgencyAndAccount';
  if (!isTransfer && !isPixAgency) return { id: values.id, operationTypeValue: values.operationTypeValue, pixKeyTypeValue: values.pixKeyTypeValue, keyPix };
  const account = optionalNumber(values.account);
  return {
    id: values.id,
    bankCode: values.bankCode ? Number(values.bankCode) : undefined,
    bankIspb: values.bankIspb ? values.bankIspb : undefined,
    account: account !== null && account !== 0 ? String(account) : undefined,
    accountDigit: values.accountDigit,
    agency: values.agency,
    agencyDigit: values.agencyDigit,
    type: values.type,
    jointAccount: values.jointAccount,
    operationTypeValue: values.operationTypeValue,
    pixKeyTypeValue: values.pixKeyTypeValue,
    keyPix,
  };
}

/** Fatos de visibilidade das 4 ações da trilha, lidos do `GET` (CreditNoteButtons.tsx:201-208, 450-471). */
export const isLiquidationSchedule = (note: CreditNote | undefined) => note?.liquidationMode === 'LiquidationSchedule';

// ─── opções dos formulários (pelo valor técnico, U33) ───────────────────────

export interface LiquidationOption<V extends string = string> {
  readonly value: V;
  readonly label: MessageDescriptor;
}

/** "Ação" de cada item (ApproveLiquidationScheduleForm.tsx:51-70); a automática só com conta externa. */
export const SCHEDULE_ACTIONS: readonly LiquidationOption<ScheduleAction>[] = [
  { value: 'NoAction', label: msg`Manter pendente` },
  { value: 'ManualLiquidation', label: msg`Marcar como liquidado` },
  { value: 'Failed', label: msg`Reprovar` },
  { value: 'AutomaticLiquidation', label: msg`Liquidar automaticamente` },
];

/** "Ação" da conta transitória (WrapperForm.tsx:46-51). */
export const TRANSITORY_ACTIONS: readonly LiquidationOption<TransitoryAction>[] = [
  { value: 'ManualLiquidation', label: msg`Liquidado manualmente` },
  { value: 'AutomaticLiquidation', label: msg`Liquidar automaticamente` },
];

/** Rodapé de cada item pela ação escolhida (Form/actionsText.tsx:20-48). */
export const SCHEDULE_ACTION_RESULT: Readonly<Record<ScheduleAction, { readonly label: MessageDescriptor; readonly tone: 'ok' | 'err' | 'accent' | 'warn' }>> = {
  ManualLiquidation: { label: msg`Pago`, tone: 'ok' },
  Failed: { label: msg`Reprovar`, tone: 'err' },
  AutomaticLiquidation: { label: msg`Liquidar`, tone: 'accent' },
  NoAction: { label: msg`Pendente`, tone: 'warn' },
};

/** "Tipo de Operação" do `BankDetails` (BankDetails.tsx:42-47), trocável pelo `FORM_FIELDS`. */
export const OPERATION_TYPES: readonly LiquidationOption[] = [
  { value: 'Transfer', label: msg`Transferência` },
  { value: 'Pix', label: msg`Pix` },
];

/** `pixKeyTypeOpt(false)` (services/accounts/bankAccount/types/generics.ts:265-277): a revisão abre como PF. */
export const PIX_KEY_TYPES: readonly LiquidationOption[] = [
  { value: 'NaturalRegistrationNumber', label: msg`CPF` },
  { value: 'Phone', label: msg`Telefone` },
  { value: 'Email', label: msg`Email` },
  { value: 'Automatic', label: msg`Aleatória` },
  { value: 'AgencyAndAccount', label: msg`Agência e Conta` },
];

/** `pixKeyTypeOpt(true)` (generics.ts:265-277): a pessoa jurídica troca o CPF pelo CNPJ. */
export const LEGAL_PIX_KEY_TYPES: readonly LiquidationOption[] = [{ value: 'LegalRegistrationNumber', label: msg`CNPJ` }, ...PIX_KEY_TYPES.slice(1)];

/** `typeAccountsLegalPerson` (contexts/bankAccount/bankAccountType.tsx:5-10). */
export const LEGAL_ACCOUNT_TYPES: readonly LiquidationOption[] = [
  { value: 'EscrowAccount', label: msg`Conta Escrow` },
  { value: 'NaturalSimpleAccount', label: msg`Conta Pagamento` },
  { value: 'LegalCheckingAccount', label: msg`Conta Corrente` },
  { value: 'LegalSavingsAccount', label: msg`Conta Poupança` },
];

/** `typeAccountsNaturalPerson` (contexts/bankAccount/bankAccountType.ts:12-17), trocável pelo `FORM_FIELDS` (`type`). */
export const NATURAL_ACCOUNT_TYPES: readonly LiquidationOption[] = [
  { value: 'NaturalSimpleAccount', label: msg`Conta Pagamento` },
  { value: 'NaturalCheckingAccount', label: msg`Conta Corrente` },
  { value: 'NaturalSavingsAccount', label: msg`Conta Poupança` },
  { value: 'EscrowAccount', label: msg`Conta Escrow` },
];

/** Os tipos de chave cujo campo tem máscara e grava só dígitos na tela. */
export const MASKED_PIX_KEYS: Readonly<Record<string, 'phone' | 'cpf' | 'cnpj'>> = { Phone: 'phone', NaturalRegistrationNumber: 'cpf', LegalRegistrationNumber: 'cnpj' };

export const TRANSFER = 'Transfer';
export const PIX = 'Pix';
export const PIX_AGENCY_AND_ACCOUNT = 'AgencyAndAccount';
export const TRANSITORY_AUTOMATIC: TransitoryAction = 'AutomaticLiquidation';
export const SCHEDULE_FAILED: ScheduleAction = 'Failed';
export const SCHEDULE_NO_ACTION: ScheduleAction = 'NoAction';
